/**
 * Red Flags Analyzer for Visa Interview Preparation
 *
 * Detects potential issues in LinkedIn data that could raise concerns
 * during visa interviews.
 */

import type {
  RedFlag,
  Severity,
  Position,
  JobApplication,
  LinkedInData,
  TimelineAnalysis,
} from '../types.js';

/**
 * Configuration for red flag detection thresholds
 */
export interface RedFlagConfig {
  /** Gap in days considered significant (default: 90 days) */
  significantGapDays: number;
  /** Tenure in days considered short (default: 365 days = 12 months) */
  shortTenureDays: number;
  /** Number of short tenures before flagging (default: 2) */
  shortTenureThreshold: number;
  /** Number of job changes per year considered frequent (default: 2) */
  frequentJobChangesPerYear: number;
  /** Overlap in days considered significant (default: 30 days) */
  significantOverlapDays: number;
}

const DEFAULT_CONFIG: RedFlagConfig = {
  significantGapDays: 90,
  shortTenureDays: 365,
  shortTenureThreshold: 2,
  frequentJobChangesPerYear: 2,
  significantOverlapDays: 30,
};

/**
 * Analyze LinkedIn data for visa interview red flags
 */
export function analyzeRedFlags(
  data: LinkedInData,
  timeline: TimelineAnalysis,
  config: Partial<RedFlagConfig> = {}
): RedFlag[] {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const flags: RedFlag[] = [];

  // Employment gap analysis
  flags.push(...analyzeEmploymentGaps(timeline, cfg));

  // Short tenure analysis
  flags.push(...analyzeShortTenures(data.positions, timeline, cfg));

  // Overlapping employment analysis
  flags.push(...analyzeOverlappingEmployment(timeline, cfg));

  // Side business analysis
  flags.push(...analyzeSideBusinesses(data.positions));

  // Sponsorship requirement analysis
  flags.push(...analyzeSponsorshipRequirements(data.jobApplications));

  // Data inconsistency analysis
  flags.push(...analyzeDataInconsistencies(data));

  // Frequent job changes analysis
  flags.push(...analyzeFrequentJobChanges(data.positions, timeline, cfg));

  // Sort by severity (red first, then orange, then green)
  const severityOrder: Record<Severity, number> = { red: 0, orange: 1, green: 2 };
  flags.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  return flags;
}

/**
 * Analyze employment gaps
 */
function analyzeEmploymentGaps(
  timeline: TimelineAnalysis,
  config: RedFlagConfig
): RedFlag[] {
  const flags: RedFlag[] = [];

  for (const gap of timeline.gaps) {
    if (gap.durationDays >= config.significantGapDays) {
      const months = Math.round(gap.durationDays / 30);
      const severity: Severity = gap.durationDays > 180 ? 'red' : 'orange';

      flags.push({
        severity,
        category: 'employment_gap',
        description: `Employment gap of ${months} months detected`,
        evidence: `Gap between: ${formatDateRange(gap.startDate, gap.endDate)}. ` +
                  `Previous: ${gap.label}`,
        recommendation: severity === 'red'
          ? 'Prepare a detailed explanation for this significant gap. Consider documenting any activities during this period (education, family care, health, travel, etc.).'
          : 'Be prepared to briefly explain this gap if asked. Having a clear, honest explanation is important.',
        dateRange: {
          start: gap.startDate?.toISOString(),
          end: gap.endDate?.toISOString(),
        },
      });
    }
  }

  return flags;
}

/**
 * Check if a position title indicates it's an internship
 */
function isInternshipPosition(title: string): boolean {
  const lower = title.toLowerCase();
  return lower.includes('intern') || lower.includes('trainee') || lower.includes('apprentice') ||
         lower.includes('co-op') || lower.includes('summer') || lower.includes('student');
}

/**
 * Analyze short tenures
 * NOTE: Internships are excluded - they're SUPPOSED to be short (2-3 months)
 */
function analyzeShortTenures(
  positions: Position[],
  _timeline: TimelineAnalysis,
  config: RedFlagConfig
): RedFlag[] {
  const flags: RedFlag[] = [];

  // Filter to non-internship positions only
  const nonInternPositions = positions.filter(p => !isInternshipPosition(p.title || ''));

  const shortNonInternPositions = nonInternPositions.filter(p => {
    if (!p.startedOn) return false;
    const endDate = p.finishedOn || new Date();
    const days = Math.floor((endDate.getTime() - p.startedOn.getTime()) / (1000 * 60 * 60 * 24));
    return days < config.shortTenureDays;
  });

  const shortTenureCount = shortNonInternPositions.length;

  if (shortTenureCount >= config.shortTenureThreshold) {
    const positionList = shortNonInternPositions
      .map(p => `${p.companyName} (${p.title}): ${formatDuration(p.startedOn, p.finishedOn)}`)
      .join('; ');

    flags.push({
      severity: shortTenureCount >= 3 ? 'red' : 'orange',
      category: 'short_tenure',
      description: `${shortTenureCount} positions with tenure less than 12 months`,
      evidence: positionList,
      recommendation: 'Prepare explanations for why you left each position early. ' +
                      'Emphasize career growth, company circumstances (layoffs, acquisitions), ' +
                      'or other legitimate reasons. Avoid appearing as a "job hopper".',
    });
  }

  return flags;
}

/**
 * Check if an entry is an internship
 */
function isInternship(label: string): boolean {
  const lower = label.toLowerCase();
  return lower.includes('intern') || lower.includes('trainee') || lower.includes('apprentice');
}

/**
 * Check if an entry is education-related (student position)
 */
function isEducationRelated(label: string, organization: string): boolean {
  const labelLower = label.toLowerCase();
  const orgLower = organization.toLowerCase();

  const eduLabels = ['student', 'graduate', 'research assistant', 'teaching assistant', 'fellow'];
  const eduOrgs = ['university', 'college', 'institute', 'school'];

  return eduLabels.some(e => labelLower.includes(e)) ||
         eduOrgs.some(e => orgLower.includes(e));
}

/**
 * Analyze overlapping employment dates
 */
function analyzeOverlappingEmployment(
  timeline: TimelineAnalysis,
  config: RedFlagConfig
): RedFlag[] {
  const flags: RedFlag[] = [];

  for (const overlap of timeline.overlaps) {
    if (overlap.overlapDays >= config.significantOverlapDays) {
      const months = Math.round(overlap.overlapDays / 30);

      const label1 = overlap.entry1.label;
      const label2 = overlap.entry2.label;
      const org1 = overlap.entry1.organization;
      const org2 = overlap.entry2.organization;

      // Check if one is an internship and the other is education-related
      // This is NORMAL (summer internships during grad school)
      const isInternshipDuringSchool =
        (isInternship(label1) && isEducationRelated(label2, org2)) ||
        (isInternship(label2) && isEducationRelated(label1, org1));

      if (isInternshipDuringSchool) {
        // Skip - this is completely normal
        continue;
      }

      // Check if one might be a side business
      const sideBusinessIndicators = ['founder', 'co-founder', 'owner', 'self-employed', 'freelance'];
      const isSideBusiness = sideBusinessIndicators.some(ind =>
        label1.toLowerCase().includes(ind) ||
        label2.toLowerCase().includes(ind)
      );

      const severity: Severity = isSideBusiness ? 'orange' : 'red';

      flags.push({
        severity,
        category: 'overlapping_employment',
        description: `Overlapping employment of ${months} months detected`,
        evidence: `"${org1}" (${label1}) overlaps with ` +
                  `"${org2}" (${label2}). ` +
                  `Overlap period: ${overlap.overlapDays} days.`,
        recommendation: isSideBusiness
          ? 'Be prepared to explain how you managed both roles. Ensure this arrangement was allowed by your primary employer.'
          : 'Clarify if these were part-time, transition periods, or if dates are incorrect. ' +
            'Overlapping full-time positions can raise questions about data accuracy.',
        dateRange: {
          start: overlap.entry1.startDate?.toISOString(),
          end: overlap.entry2.endDate?.toISOString(),
        },
      });
    }
  }

  return flags;
}

/**
 * Analyze side businesses and entrepreneurial activities
 */
function analyzeSideBusinesses(positions: Position[]): RedFlag[] {
  const flags: RedFlag[] = [];

  const sideBusinesses = positions.filter(p => p.isSideBusiness);
  const hasCurrentJob = positions.some(p => !p.finishedOn && !p.isSideBusiness);

  for (const business of sideBusinesses) {
    // Check if side business is concurrent with employment
    const isOngoing = !business.finishedOn;
    const overlapsWithEmployment = positions.some(p =>
      !p.isSideBusiness &&
      p.startedOn &&
      business.startedOn &&
      p.startedOn <= (business.finishedOn || new Date()) &&
      (p.finishedOn || new Date()) >= business.startedOn
    );

    if (isOngoing && hasCurrentJob && overlapsWithEmployment) {
      flags.push({
        severity: 'orange',
        category: 'side_business',
        description: 'Active side business while employed',
        evidence: `"${business.companyName}" (${business.title}) appears to be ongoing ` +
                  `alongside primary employment. Started: ${formatDate(business.startedOn)}.`,
        recommendation: 'Consular officers may question if side businesses indicate intent to stay ' +
                        'permanently or if they violate visa terms. Ensure your primary job is your ' +
                        'focus and any side activities comply with visa restrictions.',
      });
    }

    // Check for indicators of immigrant intent through business
    const businessDesc = (business.description || '').toLowerCase();
    const immigrantIntentIndicators = [
      'permanent', 'long-term', 'establish', 'citizenship', 'settle',
      'american dream', 'build in america', 'us expansion',
    ];

    if (immigrantIntentIndicators.some(ind => businessDesc.includes(ind))) {
      flags.push({
        severity: 'red',
        category: 'immigration_intent',
        description: 'Business description suggests permanent immigration intent',
        evidence: `Business "${business.companyName}" description may suggest intent to settle permanently.`,
        recommendation: 'For non-immigrant visas, avoid language that suggests permanent settlement plans. ' +
                        'Focus on temporary assignment aspects and ties to home country.',
      });
    }
  }

  return flags;
}

/**
 * Analyze job applications for sponsorship requirements
 */
function analyzeSponsorshipRequirements(applications: JobApplication[]): RedFlag[] {
  const flags: RedFlag[] = [];

  const sponsorshipApps = applications.filter(app => app.requiresSponsorship);

  if (sponsorshipApps.length > 0) {
    // Group by year to detect patterns
    const byYear = new Map<number, JobApplication[]>();
    for (const app of sponsorshipApps) {
      if (app.applicationDate) {
        const year = app.applicationDate.getFullYear();
        if (!byYear.has(year)) byYear.set(year, []);
        byYear.get(year)!.push(app);
      }
    }

    // Recent applications (last 2 years) are more relevant
    const currentYear = new Date().getFullYear();
    const recentApps = sponsorshipApps.filter(app =>
      app.applicationDate && app.applicationDate.getFullYear() >= currentYear - 2
    );

    if (recentApps.length > 5) {
      flags.push({
        severity: 'orange',
        category: 'sponsorship_required',
        description: 'Multiple recent job applications indicating sponsorship requirement',
        evidence: `${recentApps.length} job applications in the last 2 years indicate "Yes" for ` +
                  `requiring visa sponsorship. Companies include: ${recentApps.slice(0, 5).map(a => a.companyName).join(', ')}...`,
        recommendation: 'This is normal for work visa holders, but be prepared to discuss your ' +
                        'current visa status and employment stability if asked.',
      });
    }

    // Check for any answers suggesting unauthorized work desire
    for (const app of applications) {
      const qa = app.questionAndAnswers.toLowerCase();
      if (
        (qa.includes('start immediately') && qa.includes(':yes')) &&
        app.requiresSponsorship
      ) {
        // This might indicate urgency that could be questioned
        // Only flag if there's a pattern
      }
    }
  }

  return flags;
}

/**
 * Analyze data inconsistencies across LinkedIn export
 */
function analyzeDataInconsistencies(data: LinkedInData): RedFlag[] {
  const flags: RedFlag[] = [];

  // Check for profile vs positions inconsistencies
  if (data.profile && data.positions.length > 0) {
    // Location inconsistencies
    const profileLocation = data.profile.geoLocation?.toLowerCase() || '';
    const currentPosition = data.positions.find(p => !p.finishedOn);

    if (currentPosition) {
      const positionLocation = currentPosition.location?.toLowerCase() || '';

      // Check if profile says one place but work is in another
      if (profileLocation && positionLocation &&
          !locationsMatch(profileLocation, positionLocation)) {
        flags.push({
          severity: 'green',
          category: 'data_inconsistency',
          description: 'Profile location differs from current work location',
          evidence: `Profile shows "${data.profile.geoLocation}" but current position at ` +
                    `"${currentPosition.companyName}" shows "${currentPosition.location}".`,
          recommendation: 'Ensure your profile location is current. Inconsistencies may raise minor questions.',
        });
      }
    }
  }

  // Check for date inconsistencies in job applications vs positions
  // Note: We track position companies for potential future analysis
  for (const app of data.jobApplications) {
    // Check if applied to current employer (might indicate unhappiness)
    const currentPosition = data.positions.find(p => !p.finishedOn);
    if (currentPosition &&
        app.companyName?.toLowerCase() === currentPosition.companyName?.toLowerCase() &&
        app.applicationDate &&
        currentPosition.startedOn &&
        app.applicationDate > currentPosition.startedOn) {
      // Applied to own company after starting - probably internal transfer, not a flag
    }
  }

  // Check for education dates vs work dates logic
  const earliestWork = data.positions
    .filter(p => p.startedOn)
    .sort((a, b) => (a.startedOn?.getTime() || 0) - (b.startedOn?.getTime() || 0))[0];

  const latestEducation = data.education
    .filter(e => e.endDate)
    .sort((a, b) => (b.endDate?.getTime() || 0) - (a.endDate?.getTime() || 0))[0];

  if (earliestWork && latestEducation &&
      earliestWork.startedOn && latestEducation.endDate &&
      earliestWork.startedOn < latestEducation.endDate) {
    // Started working before finishing education - common and usually fine (internships, part-time)
    // Only flag if significant overlap and both appear full-time
    const overlap = Math.floor(
      (latestEducation.endDate.getTime() - earliestWork.startedOn.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (overlap > 365 * 2) { // More than 2 years overlap
      flags.push({
        severity: 'green',
        category: 'data_inconsistency',
        description: 'Significant overlap between education and employment',
        evidence: `Started at "${earliestWork.companyName}" (${formatDate(earliestWork.startedOn)}) ` +
                  `while still in school at "${latestEducation.schoolName}" until ${formatDate(latestEducation.endDate)}.`,
        recommendation: 'This is often normal (part-time work, internships). Be prepared to clarify if asked.',
      });
    }
  }

  return flags;
}

/**
 * Analyze frequency of job changes
 */
function analyzeFrequentJobChanges(
  _positions: Position[],
  timeline: TimelineAnalysis,
  config: RedFlagConfig
): RedFlag[] {
  const flags: RedFlag[] = [];

  if (timeline.positionCount >= 3 && timeline.totalCareerSpanDays > 365) {
    const yearsOfCareer = timeline.totalCareerSpanDays / 365;
    const changesPerYear = timeline.positionCount / yearsOfCareer;

    if (changesPerYear >= config.frequentJobChangesPerYear) {
      flags.push({
        severity: changesPerYear >= 3 ? 'red' : 'orange',
        category: 'frequent_job_changes',
        description: `High job change frequency: ${changesPerYear.toFixed(1)} positions per year`,
        evidence: `${timeline.positionCount} positions over ${yearsOfCareer.toFixed(1)} years. ` +
                  `Average tenure: ${Math.round(timeline.averageTenureDays / 30)} months.`,
        recommendation: 'Prepare to explain your career trajectory positively. Emphasize skills gained, ' +
                        'promotions, company circumstances, or strategic career moves rather than instability.',
      });
    }
  }

  return flags;
}

/**
 * Check if two location strings refer to roughly the same place
 */
function locationsMatch(loc1: string, loc2: string): boolean {
  const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

  const n1 = normalize(loc1);
  const n2 = normalize(loc2);

  // Direct match
  if (n1.includes(n2) || n2.includes(n1)) return true;

  // Common aliases
  const aliases: Record<string, string[]> = {
    'sanfrancisco': ['bayarea', 'sf', 'sanfranciscobayarea'],
    'newyork': ['nyc', 'newyorkcity', 'manhattan'],
    'losangeles': ['la', 'losangelesca'],
    'sanjose': ['siliconvalley', 'southbay'],
  };

  for (const [key, values] of Object.entries(aliases)) {
    const allVariants = [key, ...values];
    const n1Match = allVariants.some(v => n1.includes(v));
    const n2Match = allVariants.some(v => n2.includes(v));
    if (n1Match && n2Match) return true;
  }

  return false;
}

/**
 * Format a date for display
 */
function formatDate(date: Date | null): string {
  if (!date) return 'Unknown';
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * Format a date range for display
 */
function formatDateRange(start: Date | null, end: Date | null): string {
  return `${formatDate(start)} to ${formatDate(end)}`;
}

/**
 * Format duration between two dates
 */
function formatDuration(start: Date | null, end: Date | null): string {
  if (!start) return 'Unknown duration';
  const endDate = end || new Date();
  const days = Math.floor((endDate.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  const months = Math.round(days / 30);

  if (months < 12) {
    return `${months} months`;
  } else {
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (remainingMonths === 0) {
      return `${years} year${years > 1 ? 's' : ''}`;
    }
    return `${years} year${years > 1 ? 's' : ''} ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
  }
}

/**
 * Get overall risk assessment from flags
 */
export function calculateOverallRisk(flags: RedFlag[]): Severity {
  const redCount = flags.filter(f => f.severity === 'red').length;
  const orangeCount = flags.filter(f => f.severity === 'orange').length;

  if (redCount >= 2) return 'red';
  if (redCount >= 1 || orangeCount >= 3) return 'orange';
  return 'green';
}

/**
 * Generate recommended actions based on flags
 */
export function generateRecommendedActions(flags: RedFlag[]): string[] {
  const actions: string[] = [];
  const categories = new Set(flags.map(f => f.category));

  if (categories.has('employment_gap')) {
    actions.push('Prepare written explanations for employment gaps with supporting documentation if available');
  }

  if (categories.has('short_tenure') || categories.has('frequent_job_changes')) {
    actions.push('Create a narrative that explains career progression positively - focus on growth, not instability');
  }

  if (categories.has('side_business')) {
    actions.push('Review visa terms regarding outside employment and business activities');
    actions.push('Ensure employer is aware of and approves any side business activities');
  }

  if (categories.has('overlapping_employment')) {
    actions.push('Verify all employment dates are accurate and update LinkedIn if needed');
    actions.push('Prepare explanation for any legitimate overlapping roles (transition periods, part-time work)');
  }

  if (categories.has('sponsorship_required')) {
    actions.push('Have current visa documents and employment verification ready');
  }

  if (categories.has('anti_us_sentiment') || categories.has('political_activism')) {
    actions.push('Review all public social media content for potentially problematic posts');
    actions.push('Consider removing or archiving controversial content before interview');
  }

  if (categories.has('immigration_intent')) {
    actions.push('Prepare documentation of ties to home country (property, family, return plans)');
    actions.push('Ensure LinkedIn profile language aligns with non-immigrant intent if applicable');
  }

  // Always add these general recommendations
  actions.push('Practice explaining your career story clearly and confidently');
  actions.push('Ensure all information across documents (resume, LinkedIn, forms) is consistent');

  return [...new Set(actions)]; // Remove duplicates
}
